import { useLoaderData } from 'react-router';

import type { loader } from '~/routes/_app.project.$urlSegment._index';
import {
    GetInTouchRowContent,
    useEnquiryFunnelStages,
    type NumberedAccordionRow,
} from '~/ui-rocco/pages/detail-shared';
import { useProjectBookMeeting } from './use-project-book-meeting';
import {
    ABOUT_PROJECT_ROW_TITLE,
    ABOUT_PROJECT_ROW_ID,
    BOOK_MEETING_ROW_TITLE,
    BOOK_MEETING_ROW_ID,
    useBookMeetingAccordion,
} from './use-book-meeting-accordion';
import { getDetailsItems } from '@modules/project/component/details/util/get-details-items';
import { AboutProjectRowContent } from '../sections/AboutProjectRowContent';
import { BookMeetingRowContent } from '~/modules-rocco/booking';
import { ProjectTeamRowContent } from '../sections/ProjectTeamRowContent';

/**
 * Builds the project's numbered-accordion rows (About the Project / Project Team
 * / Book a Meeting) from the loader. Shared between the accordion section and the
 * About-the-Supplier section, which numbers itself as the next item after the
 * accordion (the numbered sequence is exactly these rows — mirrors the product).
 */
export const useProjectAccordionRows = (): { rows: NumberedAccordionRow[] } => {
    const { project, projectTeamMembers } = useLoaderData<typeof loader>();
    const { canBookDirect, sessions: bookingSessions } =
        useProjectBookMeeting();
    // Eager availability prefetch, owned by the provider (single fetch on page
    // load) and passed to the booking row as controlled state. See BC-4603.
    const { meetingAvailability } = useBookMeetingAccordion();
    // Enquiry funnel one-shots live here rather than in the row, which the
    // accordion unmounts when closed; the hook resets them per page view.
    const firedFunnelStages = useEnquiryFunnelStages('project', project.ID);

    // (01) About the Project — editorial lead statement over the description.
    // Drops the boilerplate "ArchiPro Project Summary -" prefix some records
    // carry, as the Story section does.
    const aboutHeading = (project.Subheading ?? '')
        .replace(/^\s*ArchiPro Project Summary\s*[-–—:]\s*/i, '')
        .trim();
    const aboutHtml = (project.Content ?? '').trim();

    // The project spec table (Title / Builder / Category / Completed / …) — the
    // same rows the legacy details list builds, folded in under the description
    // in the (01) row (mirrors the product page's (01) Product Details table).
    // Awards render as their own moment elsewhere, so they are dropped here.
    const specItems = getDetailsItems(project, {
        includeNewFields: true,
    }).filter((item) => item.itemName && item.itemName !== 'Awards');
    const hasAbout = Boolean(aboutHeading || aboutHtml || specItems.length > 0);

    // (02) Project Team — loaded, public-only team members (BC-4602).
    const teamGroups = projectTeamMembers ?? [];
    const hasTeamMembers = teamGroups.some(
        (group) => (group.TeamMembers ?? []).length > 0,
    );

    const rows: NumberedAccordionRow[] = [];
    if (hasAbout) {
        rows.push({
            title: ABOUT_PROJECT_ROW_TITLE,
            id: ABOUT_PROJECT_ROW_ID,
            // Header clearance so the summary's "Read more" anchor lands the row
            // heading below any sticky top nav.
            itemClassName: 'scroll-mt-24',
            content: (
                <AboutProjectRowContent
                    heading={aboutHeading || undefined}
                    contentHtml={aboutHtml || undefined}
                    specItems={specItems}
                />
            ),
        });
    }
    if (hasTeamMembers) {
        rows.push({
            title: 'Project Team',
            content: <ProjectTeamRowContent groups={teamGroups} />,
        });
    }
    // (03) Book a Meeting / Get in touch — always offered. With a bookable
    // session the row expands to the inline calendar (BC-4603); without one it
    // reads "Get in touch" and expands to the professional's location map +
    // inline enquiry form (BC-4638). The title constant stays the stable
    // open-state key; only the displayed heading changes.
    rows.push({
        title: BOOK_MEETING_ROW_TITLE,
        displayTitle: canBookDirect
            ? BOOK_MEETING_ROW_TITLE
            : 'Like this Project? Get in touch',
        id: BOOK_MEETING_ROW_ID,
        // Header clearance so the opened row clears any sticky top nav.
        itemClassName: 'scroll-mt-24',
        content: canBookDirect ? (
            <BookMeetingRowContent
                sessions={bookingSessions}
                availabilityState={meetingAvailability}
            />
        ) : (
            <GetInTouchRowContent
                professional={project.Professional}
                itemType="project"
                siteTreeID={project.ID}
                itemTitle={project.Title}
                itemDescription="this project"
                relatedItem={{
                    id: project.ID,
                    type: 'project',
                    title: project.Title,
                    category: project.categoryPath,
                }}
                firedFunnelStages={firedFunnelStages}
                theme="dark"
            />
        ),
    });

    return { rows };
};
